<template>
    <div class="unit-settings">
        <Card>
            <Heading
                :labelTitle="$t.__('settings.units.unit_settings.title')"
                variant="h2"
                :prefixColoredIcon="{ icon: 'tool', color: 'brand' }"
            />
            <div class="unit-settings__row">
                <CompassFormItem :label="$t.__('settings.units.unit_settings.unit_column.label')">
                    <CompassSkeleton v-if="isLoadingUnitSettings" size="x-large" />
                    <CompassRadioGroup v-else v-model="unitColumn" label="" direction="horizontal">
                        <CompassRadio
                            v-for="item in Object.keys(unitColumns)"
                            :key="unitColumns[item].value"
                            :label="unitColumns[item].label"
                            :modelValue="unitColumns[item].value"
                        />
                    </CompassRadioGroup>
                </CompassFormItem>
                <CompassFormItem
                    :label="$t.__('settings.units.unit_settings.unit_precision.label')"
                    class="unit-precision"
                >
                    <CompassSkeleton v-if="isLoadingUnitSettings" size="x-large" />
                    <CompassSelect v-else v-model="unitAmountPrecision">
                        <CompassDropdownSelectOption
                            v-for="unitAmount in unitAmountPrecisionList"
                            :key="unitAmount"
                            :label="formatPrecision(unitAmount)"
                            :value="unitAmount"
                        />
                    </CompassSelect>
                </CompassFormItem>
            </div>
            <h2 class="unit-settings__card-title typographies-heading-h2">
                {{ $t.__('settings.units.measurement_units.title') }}
            </h2>
            <p class="typographies-paragraph-medium-regular">
                {{ $t.__('settings.units.measurement_units.description') }}
            </p>
            <div class="unit-settings__row">
                <CompassFormItem
                    :label="$t.__('settings.units.measurement_units.measurement_units.label')"
                >
                    <CompassSkeleton v-if="isLoadingUnitSettings" size="x-large" />
                    <CompassSelect v-else v-model="dimensionUnit">
                        <CompassDropdownSelectOption
                            v-for="item in Object.keys(dimensionUnits)"
                            :key="dimensionUnits[item].value"
                            :label="dimensionUnits[item].label"
                            :value="dimensionUnits[item].value"
                        />
                    </CompassSelect>
                </CompassFormItem>
                <CompassFormItem
                    :label="$t.__('settings.units.measurement_units.weight_units.label')"
                >
                    <CompassSkeleton v-if="isLoadingUnitSettings" size="x-large" />
                    <CompassSelect v-else v-model="weightUnit">
                        <CompassDropdownSelectOption
                            v-for="item in Object.keys(weightUnits)"
                            :key="weightUnits[item].value"
                            :label="weightUnits[item].label"
                            :value="weightUnits[item].value"
                        />
                    </CompassSelect>
                </CompassFormItem>
            </div>
            <div class="unit-settings__actions">
                <CompassButton
                    :label="$t.__('common.labels.save_changes')"
                    :disabled="isLoadingUnitSettings"
                    :loading="isSavingSettings"
                    @click="handleUnitSettingsSave"
                />
            </div>
        </Card>
        <Card>
            <Heading
                :labelTitle="$t.__('settings.units.unit_list.title')"
                variant="h2"
                :prefixColoredIcon="{ icon: 'list', color: 'brand' }"
            />
            <CompassAlert
                v-if="showUnitCode"
                customIcon="/ressources/logged/img/icon/svg/info-e-invoicing.svg"
                :description="$t.__('settings.units.unit_list.alert.description')"
                type="neutral"
                :action="{
                    label: $t.__('settings.units.unit_list.alert.action'),
                    callback: consultCodeList,
                }"
                actionPosition="bottom"
                :showClose="false"
            />
            <p class="typographies-paragraph-medium-regular">
                {{ $t.__('settings.units.unit_list.description') }}
            </p>
            <CompassTabs :key="languages.length" v-model="currentTab">
                <CompassTabPane
                    :label="$t.__('common.labels.by_default')"
                    name="default"
                    icon="flag-fill"
                    :isError="hasErrors"
                >
                    <TableLoading v-if="isLoading" />
                    <UnitTable
                        v-else
                        :units="editableUnits"
                        :canEditUnit="isAdmin"
                        :isEditable="isEditable"
                        :editMode="editMode"
                        :unit="unit"
                        :showUnitCode="showUnitCode"
                        @update:unit="handleUpdateDefaultUnit"
                        @addUnit="handleAddUnit"
                        @removeUnit="handleRemoveUnit"
                        @sort="handleSortUpdate"
                    />
                </CompassTabPane>
                <CompassTabPane
                    v-for="language in languages"
                    :key="language.id"
                    :label="language.lang"
                    :name="language.lang"
                    :customIcon="getLanguageIcon(language)"
                >
                    <TableLoading v-if="isLoading" />
                    <UnitTranslationTable
                        v-else
                        :units="editableUnits"
                        :isEditable="isEditable"
                        :editMode="editMode"
                        :language="language.id"
                        @sort="handleSortUpdate"
                        @addUnit="handleAddUnit"
                    />
                </CompassTabPane>
            </CompassTabs>
            <div class="unit-settings__actions">
                <CompassButton
                    v-if="!editMode"
                    :label="$t.__('settings.units.unit_list.edit_button')"
                    type="secondary"
                    :disabled="!isAdmin || isLoading"
                    :loading="isLoadingEditMode"
                    @click="startEditMode"
                />
                <template v-else>
                    <CompassPlainButton
                        :label="$t.__('common.actions.cancel')"
                        variant="neutral"
                        :loading="unitListSaving"
                        @click="handleCancelEditMode"
                    />
                    <CompassButton
                        :label="$t.__('common.actions.save')"
                        type="primary"
                        :loading="unitListSaving"
                        @click="handleUnitListSave"
                    />
                </template>
            </div>
        </Card>
    </div>
</template>
<script>
import Card from 'vcomponents/common/card/Card.vue';
import Heading from 'vcomponents/common/Heading/Heading.vue';
import {
    getAllUnits,
    getUnitCodeSuggestions,
    saveUnits,
    saveUnitSettings,
    getUnitSettings,
} from 'api/settings/unitService';
import LanguagesService from 'api/languages/languagesService';
import UnitTable from 'vcomponents/preferences/Units/UnitTable.vue';
import UnitTranslationTable from 'vcomponents/preferences/Units/UnitTranslationTable.vue';
import TableLoading from 'vcomponents/common/table/TableLoading.vue';
import { createNamespacedHelpers } from 'vuex';
const { mapState: mapStateUser } = createNamespacedHelpers('user');
const { mapGetters: mapGettersSystem } = createNamespacedHelpers('system');
import Feature from 'Services/Feature';

export default {
    components: {
        Card,
        UnitTable,
        UnitTranslationTable,
        TableLoading,
        Heading,
    },
    data: function () {
        return {
            unit: 0,
            unitColumn: '',
            weightUnit: '',
            dimensionUnit: '',
            unitAmountPrecision: 'default',
            units: [],
            editableUnits: [],
            suggestions: [],
            languages: [],
            currentTab: 'default',
            editMode: false,
            isLoading: false,
            isLoadingUnitSettings: false,
            isSavingSettings: false,
            isLoadingEditMode: false,
            unitListSaving: false,
            controller: new AbortController(),
            tempCounter: 0,
            hasErrors: false,
        };
    },
    computed: {
        ...mapStateUser(['isAdmin']),
        ...mapGettersSystem({
            dimensionUnits: 'getDimensionUnits',
            unitColumns: 'getUnitColumns',
            weightUnits: 'getWeightUnits',
            unitAmountPrecisionList: 'getUnitAmountPrecisionList',
        }),
        isEditable() {
            return this.isAdmin && this.editMode;
        },
        showUnitCode() {
            return Feature.isEnabled('einvoicing-units-parameters');
        },
    },
    async created() {
        this.isLoading = true;
        this.isLoadingUnitSettings = true;
        getUnitSettings()
            .then(({ data }) => {
                this.unit = data.unit_id;
                this.unitColumn = data.unit_display_column;
                this.weightUnit = data.weight_unit;
                this.dimensionUnit = data.dimension_unit;
                this.unitAmountPrecision = data.unit_price_precision ?? 'default';
            })
            .catch(() => {
                this.$pushToast({
                    type: 'error',
                    icon: true,
                    text: this.$t.__('settings.units.settings_fetch_error'),
                });
            })
            .finally(() => {
                this.isLoadingUnitSettings = false;
            });

        try {
            this.languages = await LanguagesService.getAllLanguages();
        } catch (error) {
            this.$pushToast({
                type: 'error',
                icon: true,
                text: this.$t.__('settings.units.unit_list.languages.fetch_error'),
            });
        }
        getAllUnits({
            order: 'rank',
            limit: 100,
            embed: ['translations'],
        })
            .then((data) => {
                this.units = data;
                this.initEditableUnits(data);
            })
            .catch(() => {
                this.$pushToast({
                    type: 'error',
                    icon: true,
                    text: this.$t.__('settings.units.unit_list.fetch_error'),
                });
            })
            .finally(() => {
                this.isLoading = false;
            });
    },
    methods: {
        getLanguageIcon(language) {
            return `/ressources/public/img/flags/${language.flag ?? 'blank'}.png`;
        },
        formatPrecision: function (value) {
            return value === 'default' ? this.$t.__('common.labels.default_value') : value;
        },
        consultCodeList() {
            console.log('consulting code list');
        },
        initEditableUnits(data = this.units) {
            this.editableUnits = JSON.parse(
                JSON.stringify(
                    data.map((item) => {
                        const hasAllTranslations = this.languages.every((lang) =>
                            item._embed.translations.some(
                                (trans) =>
                                    trans.lang_id === lang.id &&
                                    typeof trans.id === 'number' &&
                                    trans.value !== '',
                            ),
                        );
                        return hasAllTranslations
                            ? item
                            : {
                                  ...item,
                                  _embed: {
                                      ...item._embed,
                                      translations: [
                                          ...item._embed.translations.filter(
                                              (translation) => typeof translation.id === 'number',
                                          ),
                                          ...this.languages
                                              .filter(
                                                  (lang) =>
                                                      !item._embed.translations.some(
                                                          (trans) => trans.lang_id === lang.id,
                                                      ),
                                              )
                                              .map((lang) => ({
                                                  id: null,
                                                  lang_id: lang.id,
                                                  value: '',
                                              })),
                                      ],
                                  },
                              };
                    }),
                ),
            );
        },
        async startEditMode() {
            this.isLoadingEditMode = true;
            if (this.showUnitCode) {
                try {
                    const { data } = await getUnitCodeSuggestions(
                        this.units.map((unit) => {
                            return {
                                label: unit.label,
                            };
                        }),
                    );
                    this.suggestions = data;
                } catch {
                    this.$pushToast({
                        type: 'error',
                        icon: true,
                        text: this.$t.__('settings.units.suggestions.fetch_error'),
                    });
                }
            }

            this.editableUnits = this.editableUnits.map((unit) => {
                let suggestion = null;
                if (this.suggestions.length) {
                    suggestion = this.suggestions.find(
                        (item) => item.label === unit.label && item.code,
                    );
                }
                return {
                    ...unit,
                    parameters: {
                        ...unit.parameters,
                        code: unit.parameters?.code || '',
                    },
                    _embed: {
                        ...unit._embed,
                        suggestion,
                    },
                };
            });

            this.isLoadingEditMode = false;
            this.editMode = true;
        },
        handleAddUnit() {
            this.editableUnits.push({
                id: `temp-${this.tempCounter++}`, // Temporary ID for new unit
                label: '',
                parameters: { code: '' },
                _embed: {
                    suggestion: null,
                    isLoading: false,
                    translations: this.languages.map((lang) => {
                        return {
                            id: null,
                            lang_id: lang.id,
                            value: '',
                        };
                    }),
                },
            });
        },
        handleRemoveUnit(unitId) {
            this.editableUnits = this.editableUnits.filter((unit) => unit.id !== unitId);
        },
        handleSortUpdate(sortedUnits) {
            this.editableUnits = sortedUnits;
        },
        handleUpdateDefaultUnit(data) {
            this.unit = data;
            this.controller.abort();
            this.controller = new AbortController();
            saveUnitSettings(
                {
                    unit_id: data,
                },
                this.controller.signal,
            )
                .then(() => {
                    this.$pushToast({
                        type: 'success',
                        icon: true,
                        text: this.$t.__('settings.units.default_unit.save_success'),
                    });
                })
                .catch(() => {
                    this.$pushToast({
                        type: 'error',
                        icon: true,
                        text: this.$t.__('settings.units.default_unit.save_error'),
                    });
                });
        },
        handleUnitListSave() {
            this.unitListSaving = true;
            this.hasErrors = false;
            this.editableUnits = this.editableUnits.map((unit) => {
                return {
                    ...unit,
                    _embed: {
                        ...unit._embed,
                        errors: [],
                    },
                };
            });

            const payload = this.editableUnits.map((item) => {
                return {
                    id: typeof item.id === 'string' && item.id.startsWith('temp-') ? null : item.id, // new items should not have an ID
                    label: item.label.trim(),
                    parameters: {
                        ...item.parameters,
                        code: item.parameters.code.trim() || '',
                    },
                    translations: item._embed.translations
                        .filter((translation) => translation.value !== '')
                        .map((translation) => ({
                            id: translation.id,
                            lang_id: translation.lang_id,
                            value: translation.value.trim(),
                        })),
                };
            });

            saveUnits(payload)
                .then((response) => {
                    this.$pushToast({
                        type: 'success',
                        icon: true,
                        text: this.$t.__('settings.units.unit_list.save_success'),
                    });
                    this.editMode = false;
                    this.units = response.data.data;
                    this.initEditableUnits(response.data.data);
                })
                .catch((error) => {
                    if (error.response.status === 400) {
                        this.$pushToast({
                            type: 'error',
                            icon: true,
                            text: this.$t.__('settings.units.unit_list.save_validation_error'),
                        });
                        const details = error.response.data.error.details;
                        this.hasErrors = true;
                        for (const key of Object.keys(details)) {
                            const pieces = key.split('.');
                            const index = Number(pieces[1]);
                            const field = pieces.pop();
                            const message = details[key];

                            if (!this.editableUnits[index]._embed.errors) {
                                this.editableUnits[index]._embed.errors = [];
                            }

                            this.editableUnits[index]._embed.errors.push({
                                field,
                                message,
                            });
                            this.editableUnits = [...this.editableUnits];
                        }
                    } else {
                        this.$pushToast({
                            type: 'error',
                            icon: true,
                            text: this.$t.__('settings.units.unit_list.save_error'),
                        });
                        this.editMode = false;
                        this.initEditableUnits();
                    }
                })
                .finally(() => {
                    this.unitListSaving = false;
                });
        },
        handleUnitSettingsSave: function () {
            const options = {
                unit_id: this.unit,
                unit_display_column: this.unitColumn,
                weight_unit: this.weightUnit,
                dimension_unit: this.dimensionUnit,
                unit_price_precision:
                    typeof this.unitAmountPrecision === 'string' &&
                    this.unitAmountPrecision.toLowerCase() === 'default'
                        ? null
                        : Number(this.unitAmountPrecision),
            };

            this.isSavingSettings = true;

            saveUnitSettings(options)
                .then(() => {
                    this.$pushToast({
                        type: 'success',
                        icon: true,
                        text: this.$t.__('common.labels.settings_saved'),
                    });
                })
                .catch((error) => {
                    this.$pushToast({
                        type: 'error',
                        icon: true,
                        text: error.response.data.error.message,
                    });
                })
                .finally(() => {
                    this.isSavingSettings = false;
                });
        },
        handleCancelEditMode() {
            this.editMode = false;
            this.hasErrors = false;
            this.initEditableUnits();
        },
    },
};
</script>
<style lang="scss" scoped>
.unit-settings {
    padding: 0 0 var(--spacing-4);

    &__card-title {
        margin: 0 0 var(--spacing-3);
    }

    p {
        margin: 0 0 var(--spacing-3);
    }

    &__row {
        display: flex;
        gap: var(--spacing-3);
        margin: 0 0 var(--spacing-4);

        &::v-deep {
            .compass-form-item {
                flex-shrink: 0;
                width: 220px;

                &.unit-precision {
                    width: auto;

                    .compass-select {
                        width: 160px;
                    }
                }
            }
        }
    }

    &__actions {
        display: flex;
        gap: var(--spacing-3);
        justify-content: flex-end;
    }

    &::v-deep {
        .compass-alert {
            margin: 0 0 var(--spacing-3);
        }

        .compass-tabs {
            margin: var(--spacing-4) 0;

            .compass-tab-pane {
                padding: var(--spacing-3) 0 0;
            }
        }
    }
}
</style>
